import type { RttsJobDetail, RttsStatus } from '../types';
import { getRttsMockConfig } from './rttsMock';

const LIFECYCLE: RttsStatus[] = [
  'downloading',
  'source_ready',
  'detecting',
  'detect_ready',
  'rendering',
  'delivered',
];

/** Polls between stages: advance one status every N successful polls. */
const POLLS_PER_STAGE = 2;

type MockJobState = {
  detail: RttsJobDetail;
  pollCount: number;
};

const jobs = new Map<string, MockJobState>();

function seedJob(jobId: string, initialStatus: RttsStatus = 'downloading'): MockJobState {
  const existing = jobs.get(jobId);
  if (existing) return existing;
  const state: MockJobState = {
    detail: {
      id: jobId,
      status: initialStatus,
      signed_url: null,
      expires_at: null,
      error_code: null,
      reel_duration_sec: null,
    },
    pollCount: 0,
  };
  jobs.set(jobId, state);
  return state;
}

/** Register a mock job (called from acquire / tests). */
export function seedMockJob(
  jobId: string,
  overrides: Partial<RttsJobDetail> = {}
): RttsJobDetail {
  const state = seedJob(jobId, (overrides.status as RttsStatus) || 'downloading');
  state.detail = { ...state.detail, ...overrides, id: jobId };
  state.pollCount = 0;
  jobs.set(jobId, state);
  return { ...state.detail };
}

export function clearMockJobs(): void {
  jobs.clear();
}

export function mockGetHighlightJob(
  _leagueId: string,
  jobId: string
): Promise<RttsJobDetail> {
  const config = getRttsMockConfig();
  const state = seedJob(jobId);
  state.pollCount += 1;

  if (config.forceJobFailed) {
    state.detail = {
      ...state.detail,
      status: 'failed',
      error_code: config.jobFailErrorCode || 'render_failed',
      signed_url: null,
      expires_at: null,
    };
    return Promise.resolve({ ...state.detail });
  }

  if (config.jobFailAtStatus && state.detail.status === config.jobFailAtStatus) {
    state.detail = {
      ...state.detail,
      status: 'failed',
      error_code: config.jobFailErrorCode || 'render_failed',
      signed_url: null,
      expires_at: null,
    };
    return Promise.resolve({ ...state.detail });
  }

  const current = String(state.detail.status);
  if (current === 'delivered' || current === 'failed') {
    return Promise.resolve({ ...state.detail });
  }

  const idx = LIFECYCLE.indexOf(current as RttsStatus);
  if (idx >= 0 && state.pollCount >= POLLS_PER_STAGE && idx < LIFECYCLE.length - 1) {
    state.pollCount = 0;
    const next = LIFECYCLE[idx + 1]!;
    state.detail = {
      ...state.detail,
      status: next,
    };
    if (next === 'delivered') {
      const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      state.detail = {
        ...state.detail,
        signed_url: `https://example.com/mock-reels/${encodeURIComponent(jobId)}.mp4`,
        expires_at: expires,
        reel_duration_sec: 42,
        // BACKEND-DEP: richer share surface (og image / share payload)
        share_title: null,
        share_thumbnail_url: null,
        share_url: null,
      };
    }
  }

  return Promise.resolve({ ...state.detail });
}
